import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
    opacity: string;
    blue: string;
    blueOpacity: string;
    grayOpacity: string;
    tableBlue: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface TypeText {
    white: string;
    aqua: string;
    icon: string;
    light: string;
    normal: string;
    medium: string;
    dark: string;
    strong: string;
    blue: string;
    lighter: string;
  }
}

// SETUP COLORS
const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#ACB9C9',
  600: '#637381',
  700: '#454F5B',
  800: '#2f363e',
  900: '#161C24',
};

const PRIMARY = {
  lighter: '#0878FF25',
  light: '#3389FF',
  main: '#0878FF',
  dark: '#0516b1',
  darker: '#033A9B',
  contrastText: '#FFFFFF',
};

const SECONDARY = {
  lighter: '#5AE1E234',
  light: '#29BEFF',
  main: '#5AE1E2',
  dark: '#6fdef657',
  darker: '#6fdef6',
  contrastText: '#000000',
};

const INFO = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: 'rgba(145, 224, 43, 0.13)',
  light: 'rgba(145, 224, 43, 0.26)',
  main: 'rgb(145, 224, 43)',
  dark: 'rgb(145, 224, 43)',
  darker: 'rgb(145, 224, 43)',
  contrastText: '#ffffff',
};

const WARNING = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: 'rgba(224, 43, 43, 0.13)',
  light: 'rgba(224, 43, 43, 0.26)',
  main: 'rgb(224, 43, 43)',
  dark: 'rgb(224, 43, 43)',
  darker: 'rgb(224, 43, 43)',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.2),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

// Helper function to ensure color has # prefix
const normalizeColor = (color: string): string => {
  if (!color) return color;
  return color.startsWith('#') ? color : `#${color}`;
};

export function palette(mode: 'light' | 'dark', marcaColors?: { colorBoton?: string; colorBg?: string }) {
  // Usar colores de la marca si están disponibles, sino usar el azul original de Luka
  const primaryColor = normalizeColor(marcaColors?.colorBoton || '#0878FF');
  // Solo usar colorBg de la marca en modo claro, en dark mode siempre usar #10141C
  const backgroundColor = mode === 'light' 
    ? normalizeColor(marcaColors?.colorBg || '#ffffff')
    : '#10141C';
  
  // Crear paleta primaria personalizada basada en el color de la marca
  const CUSTOM_PRIMARY = {
    lighter: alpha(primaryColor, 0.15),
    light: alpha(primaryColor, 0.7),
    main: primaryColor,
    dark: alpha(primaryColor, 0.8),
    darker: alpha(primaryColor, 0.9),
    contrastText: '#FFFFFF',
  };

  const light = {
    ...COMMON,
    primary: CUSTOM_PRIMARY,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
      white: '#FFFFFF',
      aqua: SECONDARY.main,
      icon: '#5dceff',
      light: '#E6ECF6',
      lighter: '#D4E1F1',
      normal: '#ACB9C9',
      dark: '#8091a7',
      medium: '#616F81',
      strong: '#2F363E',
      blue: CUSTOM_PRIMARY.main,
    },
    background: {
      paper: '#FFFFFF',
      default: backgroundColor,
      neutral: '#F4F6F8',
      opacity: '#eef8ff',
      blue: '#000853',
      blueOpacity: 'rgba(8, 120, 255, .15)',
      tableBlue: '#e8f2ff',
      grayOpacity: 'rgb(230, 236, 246)',
    },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  };

  const dark = {
    ...COMMON,
    primary: CUSTOM_PRIMARY,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: GREY[0],
      disabled: GREY[600],
      white: '#FFFFFF',
      icon: '#FFFFFF',
      aqua: '#FFFFFF',
      light: '#76879D',
      lighter: '#D4E1F1',
      normal: '#ABC1CE',
      medium: '#DBE6EA',
      dark: '#ebf1f3',
      strong: '#FFFFFF',
      blue: CUSTOM_PRIMARY.main,
    },
    background: {
      paper: '#1e2128',
      default: backgroundColor,
      neutral: '#10141C',
      opacity: alpha(GREY[500], 0.12),
      blue: GREY[900],
      blueOpacity: 'rgba(8, 120, 255, .15)',
      tableBlue: 'rgba(8, 120, 255, .15)',
      grayOpacity: GREY[900],
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  };

  return mode === 'light' ? light : dark;
}
